import { useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { MqttLiveFeed } from "../components/mqtt/MqttLiveFeed";
import { MqttTopicDetail } from "../components/mqtt/MqttTopicDetail";
import { MqttTopicTree } from "../components/mqtt/MqttTopicTree";
import { useAuth } from "../context/AuthContext";
import { useMqttCatalog } from "../hooks/useMqttCatalog";
import { useMqttLive } from "../hooks/useMqttLive";
import { canSyncMqttCatalog } from "../lib/access";
import type { MqttTopicEntry } from "../types/mqtt";

type TabId = "live" | "reference";

export function LiveDataPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user } = useAuth();
  const allowSync = canSyncMqttCatalog(user);
  const { catalog, loading, error, reload } = useMqttCatalog(open);
  const [selected, setSelected] = useState<MqttTopicEntry | null>(null);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState<TabId>("live");
  const [syncing, setSyncing] = useState(false);
  const [syncHint, setSyncHint] = useState<string | null>(null);

  const live = useMqttLive(selected?.pattern ?? null, open && !!selected?.pattern);

  async function syncFromGithub() {
    if (!allowSync) return;
    setSyncing(true);
    setSyncHint(null);
    try {
      const res = await fetch("/api/mqtt/catalog/sync", {
        method: "POST",
        credentials: "same-origin",
      });
      const data = (await res.json()) as {
        error?: string;
        updated?: string;
        topic_count?: number;
      };
      if (!res.ok) throw new Error(data.error ?? `${res.status} ${res.statusText}`);
      reload();
      setSyncHint(
        data.updated
          ? `Synced from GitHub · ${data.updated} · ${data.topic_count ?? "?"} topics`
          : "Synced from GitHub",
      );
    } catch (err) {
      setSyncHint(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setSyncing(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-black/20"
        aria-label="Close live data panel"
        onClick={onClose}
      />
      <aside className="relative flex h-full w-full max-w-4xl flex-col border-l border-border-subtle bg-card shadow-lg">
        <header className="flex items-center justify-between border-b border-border-subtle px-4 py-3">
          <div>
            <h2
              className="font-semibold text-text-primary"
              style={{ fontSize: "var(--font-heading)" }}
            >
              Live data
            </h2>
            <p className="text-text-tertiary" style={{ fontSize: "var(--font-micro)" }}>
              MQTT live viewer & topic reference
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-text-secondary hover:bg-page hover:text-text-primary"
            aria-label="Close"
          >
            <X size={18} strokeWidth={1.75} />
          </button>
        </header>

        {loading && (
          <p className="px-4 py-3 text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
            Loading catalog…
          </p>
        )}
        {error && (
          <p className="px-4 py-3 text-status-error" style={{ fontSize: "var(--font-body)" }}>
            {error}
          </p>
        )}

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex gap-1 border-b border-border-subtle px-4 pt-3">
            {(
              [
                { id: "live" as TabId, label: "Live" },
                { id: "reference" as TabId, label: "Reference" },
              ] as const
            ).map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setTab(id)}
                className={[
                  "rounded-t-md px-3 py-1.5 font-medium transition-colors",
                  tab === id
                    ? "bg-page text-accent"
                    : "text-text-secondary hover:text-text-primary",
                ].join(" ")}
                style={{ fontSize: "var(--font-body)" }}
              >
                {label}
              </button>
            ))}
          </div>

          {catalog ? (
            <div className="grid min-h-0 flex-1 grid-cols-[220px_1fr]">
              <div className="border-r border-border-subtle p-3">
                <MqttTopicTree
                  catalog={catalog}
                  selectedPattern={selected?.pattern ?? null}
                  onSelect={(entry) => {
                    setSelected(entry);
                    setTab("live");
                  }}
                  filter={filter}
                  onFilterChange={setFilter}
                />
              </div>

              <div
                className={[
                  "flex min-h-0 flex-1 flex-col p-4",
                  tab === "live" ? "overflow-hidden" : "overflow-y-auto",
                ].join(" ")}
              >
                {tab === "live" ? (
                  <MqttLiveFeed
                    messages={live.messages}
                    connected={live.connected}
                    error={live.error}
                    topicPattern={selected?.pattern ?? null}
                  />
                ) : (
                  <MqttTopicDetail topic={selected} />
                )}
              </div>
            </div>
          ) : (
            !loading &&
            !error && (
              <p className="px-4 py-3 text-text-secondary" style={{ fontSize: "var(--font-body)" }}>
                No MQTT catalog loaded.
              </p>
            )
          )}
        </div>

        {catalog && (
          <footer
            className="flex flex-wrap items-center justify-between gap-2 border-t border-border-subtle px-4 py-2"
            style={{ fontSize: "var(--font-micro)" }}
          >
            <span className="text-text-tertiary">
              Catalog v{catalog.version}
              {catalog.updated ? ` · updated ${catalog.updated}` : ""}
            </span>
            {allowSync && (
              <div className="flex items-center gap-2">
                {syncHint && (
                  <span
                    className={
                      syncHint.startsWith("Synced") ? "text-status-ok" : "text-status-error"
                    }
                  >
                    {syncHint}
                  </span>
                )}
                <button
                  type="button"
                  onClick={syncFromGithub}
                  disabled={syncing}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-page px-2.5 py-1 font-medium text-text-primary transition-colors hover:border-accent hover:text-accent disabled:opacity-50"
                >
                  <RefreshCw size={12} className={syncing ? "animate-spin" : ""} strokeWidth={2} />
                  {syncing ? "Syncing…" : "Sync from GitHub"}
                </button>
              </div>
            )}
          </footer>
        )}
      </aside>
    </div>
  );
}
